import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Trash2, Edit2, Save, X, Menu, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const ServiceMenuPanel = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const { getAuthHeaders } = useAuth();

  const emptyItem = {
    name: '',
    slug: '',
    icon: 'Code2',
    description: '',
    image: '',
    hero_title: '',
    hero_subtitle: '',
    hero_image: '',
    content: '',
    content_image: '',
    features: [],
    benefits: [],
    sub_items: [],
    order: 0,
    is_active: true
  };

  const emptySubItem = {
    name: '',
    slug: '',
    description: '',
    hero_title: '',
    hero_subtitle: '',
    hero_image: '',
    content: '',
    content_image: '',
    features: [],
    benefits: [],
    order: 0
  };

  const [formData, setFormData] = useState(emptyItem);

  const iconOptions = [
    'Code2', 'Brain', 'Cloud', 'Users', 'ShieldCheck', 'Briefcase', 
    'Cpu', 'Database', 'Globe', 'Layers', 'Settings', 'Zap'
  ];

  useEffect(() => {
    loadMenuItems();
  }, []);

  const loadMenuItems = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/service-menu?active_only=false`);
      const data = await response.json();
      setMenuItems(data);
    } catch (error) {
      toast.error('Failed to load service menu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: prev.slug || generateSlug(name)
    }));
  };

  // Sub-item handlers
  const addSubItem = () => {
    setFormData(prev => ({
      ...prev,
      sub_items: [...prev.sub_items, { ...emptySubItem, id: `temp-${Date.now()}`, order: prev.sub_items.length }]
    }));
  };

  const updateSubItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sub_items: prev.sub_items.map((item, i) => {
        if (i === index) {
          const updated = { ...item, [field]: value };
          if (field === 'name' && !item.slug) {
            updated.slug = generateSlug(value);
          }
          return updated;
        }
        return item;
      })
    }));
  };

  const removeSubItem = (index) => {
    setFormData(prev => ({
      ...prev,
      sub_items: prev.sub_items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingId 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/cms/service-menu/${editingId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/cms/service-menu`;
      
      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          order: parseInt(formData.order) || 0
        })
      });

      if (!response.ok) throw new Error('Failed to save');

      toast.success(editingId ? 'Menu item updated!' : 'Menu item created!');
      setFormData(emptyItem);
      setEditingId(null);
      setShowAddForm(false);
      loadMenuItems();
    } catch (error) {
      toast.error('Failed to save service menu item');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name,
      slug: item.slug,
      icon: item.icon || 'Code2',
      description: item.description || '',
      image: item.image || '',
      hero_title: item.hero_title || '',
      hero_subtitle: item.hero_subtitle || '',
      hero_image: item.hero_image || '',
      content: item.content || '',
      content_image: item.content_image || '',
      features: item.features || [],
      benefits: item.benefits || [],
      sub_items: item.sub_items || [],
      order: item.order,
      is_active: item.is_active
    });
    setEditingId(item.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/service-menu/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast.success('Menu item deleted!');
      loadMenuItems();
    } catch (error) {
      toast.error('Failed to delete menu item');
    }
  };

  const handleCancel = () => {
    setFormData(emptyItem);
    setEditingId(null);
    setShowAddForm(false);
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#320547]" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Menu size={24} className="text-[#95228A]" />
            Service Menu Management
          </CardTitle>
          {!showAddForm && (
            <Button onClick={() => setShowAddForm(true)} className="bg-[#320547] hover:bg-[#95228A]">
              <Plus size={18} className="mr-1" /> Add Menu Item
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {/* Add/Edit Form */}
          {showAddForm && (
            <form onSubmit={handleSubmit} className="mb-8 p-6 bg-gray-50 rounded-xl space-y-4">
              <h3 className="font-semibold text-lg mb-4">
                {editingId ? 'Edit Menu Item' : 'Add New Menu Item'}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Menu Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleNameChange}
                    placeholder="e.g., IT Services"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Slug *</label>
                  <Input
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    placeholder="e.g., it-services"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Icon</label>
                  <select
                    name="icon"
                    value={formData.icon}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border rounded-md text-sm h-10"
                  >
                    {iconOptions.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Brief description for this service category"
                  rows={2}
                />
              </div>

              {/* Page Content Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold mb-3 text-[#320547]">Page Content (for /services/{formData.slug || 'slug'})</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Hero Title</label>
                    <Input
                      name="hero_title"
                      value={formData.hero_title}
                      onChange={handleChange}
                      placeholder="Main heading for the service page"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Hero Image URL</label>
                    <Input
                      name="hero_image"
                      value={formData.hero_image}
                      onChange={handleChange}
                      placeholder="https://images.unsplash.com/..."
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Hero Subtitle</label>
                  <Textarea
                    name="hero_subtitle"
                    value={formData.hero_subtitle}
                    onChange={handleChange}
                    placeholder="Supporting text below the main heading"
                    rows={2}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Page Content</label>
                  <Textarea
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    placeholder="Main content for the service page (supports multiple paragraphs)"
                    rows={5}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Content Image URL</label>
                  <Input
                    name="content_image"
                    value={formData.content_image}
                    onChange={handleChange}
                    placeholder="https://images.unsplash.com/... (appears next to content)"
                  />
                  <p className="text-xs text-gray-500 mt-1">This image will appear alongside the content section</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Features (one per line)</label>
                    <Textarea
                      value={formData.features?.join('\n') || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        features: e.target.value.split('\n').filter(f => f.trim())
                      }))}
                      placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Benefits (one per line)</label>
                    <Textarea
                      value={formData.benefits?.join('\n') || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        benefits: e.target.value.split('\n').filter(b => b.trim())
                      }))}
                      placeholder="Benefit 1&#10;Benefit 2&#10;Benefit 3"
                      rows={4}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Display Order</label>
                  <Input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  <label className="text-sm font-medium">Active (visible in menu)</label>
                </div>
              </div>

              {/* Sub-items Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">Sub-menu Items</h4>
                  <Button type="button" onClick={addSubItem} variant="outline" size="sm">
                    <Plus size={16} className="mr-1" /> Add Sub-item
                  </Button>
                </div>

                {formData.sub_items.length === 0 ? (
                  <p className="text-gray-500 text-sm">No sub-items. Click "Add Sub-item" to add one.</p>
                ) : (
                  <div className="space-y-4">
                    {formData.sub_items.map((subItem, index) => (
                      <div key={subItem.id || index} className="p-4 bg-white rounded-lg border space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <GripVertical size={18} className="text-gray-400 cursor-move" />
                            <span className="font-medium text-sm text-[#320547]">Sub-item {index + 1}</span>
                          </div>
                          <Button
                            type="button"
                            onClick={() => removeSubItem(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        
                        {/* Basic Info */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <Input
                            value={subItem.name}
                            onChange={(e) => updateSubItem(index, 'name', e.target.value)}
                            placeholder="Sub-item name"
                          />
                          <Input
                            value={subItem.slug}
                            onChange={(e) => updateSubItem(index, 'slug', e.target.value)}
                            placeholder="slug"
                          />
                          <Input
                            value={subItem.description || ''}
                            onChange={(e) => updateSubItem(index, 'description', e.target.value)}
                            placeholder="Menu description"
                          />
                        </div>
                        
                        {/* Page Content for Sub-item */}
                        <details className="bg-gray-50 rounded-lg p-3">
                          <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-[#320547]">
                            Page Content (click to expand)
                          </summary>
                          <div className="mt-3 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <Input
                                value={subItem.hero_title || ''}
                                onChange={(e) => updateSubItem(index, 'hero_title', e.target.value)}
                                placeholder="Hero Title"
                              />
                              <Input
                                value={subItem.hero_image || ''}
                                onChange={(e) => updateSubItem(index, 'hero_image', e.target.value)}
                                placeholder="Hero Image URL"
                              />
                            </div>
                            <Textarea
                              value={subItem.hero_subtitle || ''}
                              onChange={(e) => updateSubItem(index, 'hero_subtitle', e.target.value)}
                              placeholder="Hero Subtitle"
                              rows={2}
                            />
                            <Textarea
                              value={subItem.content || ''}
                              onChange={(e) => updateSubItem(index, 'content', e.target.value)}
                              placeholder="Page Content"
                              rows={3}
                            />
                            <Input
                              value={subItem.content_image || ''}
                              onChange={(e) => updateSubItem(index, 'content_image', e.target.value)}
                              placeholder="Content Image URL (appears next to content)"
                            />
                            <div className="grid grid-cols-2 gap-3">
                              <Textarea
                                value={subItem.features?.join('\n') || ''}
                                onChange={(e) => updateSubItem(index, 'features', e.target.value.split('\n').filter(f => f.trim()))}
                                placeholder="Features (one per line)"
                                rows={3}
                              />
                              <Textarea
                                value={subItem.benefits?.join('\n') || ''}
                                onChange={(e) => updateSubItem(index, 'benefits', e.target.value.split('\n').filter(b => b.trim()))}
                                placeholder="Benefits (one per line)"
                                rows={3}
                              />
                            </div>
                          </div>
                        </details>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={saving} className="bg-[#320547] hover:bg-[#95228A]">
                  {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save size={18} className="mr-2" />}
                  {editingId ? 'Update Menu Item' : 'Create Menu Item'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCancel}>
                  <X size={18} className="mr-2" /> Cancel
                </Button>
              </div>
            </form>
          )}

          {/* Menu Items List */}
          {menuItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Menu size={48} className="mx-auto mb-4 opacity-30" />
              <p>No service menu items yet. Add your first one!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {menuItems.map((item) => (
                <div 
                  key={item.id} 
                  className={`rounded-xl border ${item.is_active ? 'border-gray-200 bg-white' : 'border-red-200 bg-red-50/50'}`}
                >
                  <div className="flex items-center gap-4 p-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#320547] to-[#95228A] flex items-center justify-center text-white font-bold">
                      {item.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-gray-900">{item.name}</h4>
                        <span className="text-xs text-gray-400">/{item.slug}</span>
                        {!item.is_active && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded">Inactive</span>
                        )}
                      </div>
                      <p className="text-gray-500 text-sm">{item.description || 'No description'}</p>
                      <span className="text-xs text-gray-400">{item.sub_items?.length || 0} sub-items • Order: {item.order}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.sub_items?.length > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => toggleExpand(item.id)}>
                          {expandedItems[item.id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleEdit(item)}>
                        <Edit2 size={14} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(item.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Sub-items Expanded View */}
                  {expandedItems[item.id] && item.sub_items?.length > 0 && (
                    <div className="border-t bg-gray-50 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {item.sub_items.map((subItem, idx) => (
                          <div key={subItem.id || idx} className="flex items-center gap-2 p-2 bg-white rounded border text-sm">
                            <span className="w-5 h-5 rounded bg-[#95228A]/10 text-[#95228A] flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </span>
                            <span className="font-medium">{subItem.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceMenuPanel;
